import React, {useEffect, useRef} from 'react';
import ChartJS from 'chart.js/auto';

// Интерфейс для пропсов компонента
interface ChartProps {
    // labels - массив подписей оси X
    labels: string[];
    // data - значения по оси Y (основной набор данных)
    data: number[];
    // дополнительный набор данных (например, плановый график)
    secondaryData?: number[];
    // название для второго набора данных
    secondaryLabel?: string;
    // tooltips - массив с информацией для тултипов для каждой точки
    tooltips?: string[][];
    // tooltips для второго набора данных
    secondaryTooltips?: string[][];
    // необязательный заголовок графика
    title?: string;
    // тип графика (line, bar и т.д.)
    type?: 'line' | 'bar' | 'pie' | 'doughnut' | 'radar' | 'polarArea' | 'bubble' | 'scatter';
}

export const Chart: React.FC<ChartProps> = ({labels, data, secondaryData, secondaryLabel, tooltips, secondaryTooltips, title, type = 'line'}) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const chartRef = useRef<ChartJS | null>(null);

    useEffect(() => {
        if (!canvasRef.current) return;

        // Удаляем предыдущий график, если был
        if (chartRef.current) {
            chartRef.current.destroy();
        }

        // --- КАСТОМНЫЕ ГРАДАЦИИ ПО ОСИ Y ---
        const allData = [...data, ...(secondaryData || [])];
        let maxY = Math.max(...allData, 0);
        let step = Math.ceil(maxY * 0.1) || 1;
        let minSteps = 5;
        let maxSteps = 20;
        let stepsCount = Math.ceil(maxY / step);
        if (stepsCount < minSteps && maxY > 0) {
            step = Math.ceil(maxY / minSteps);
        } else if (stepsCount > maxSteps) {
            step = Math.ceil(maxY / maxSteps);
        }
        let suggestedMax = step * Math.max(minSteps, stepsCount);
        // --- END ---
        
        // Создаем массив наборов данных
        const datasets = [
            {
                label: title || 'Фактические данные',
                data,
                fill: type === 'bar',
                borderColor: '#4285F4',
                backgroundColor: type === 'bar' ? 'rgba(66, 133, 244, 0.7)' : '#4285F4',
                pointRadius: type === 'line' ? 5 : 0,
                pointHoverRadius: type === 'line' ? 7 : 0,
                pointBackgroundColor: '#fff',
                pointBorderColor: '#4285F4',
                tension: 0.2,
                barThickness: type === 'bar' ? 20 : undefined,
                maxBarThickness: type === 'bar' ? 30 : undefined,
            }
        ];
        
        // Добавляем второй набор данных, если он есть
        if (secondaryData && secondaryData.length > 0) {
            // Используем приведение типа для всего объекта, чтобы избежать ошибок типизации
            datasets.push({
                label: secondaryLabel || 'Плановые данные',
                data: secondaryData,
                fill: false,
                borderColor: '#FF6347', // Красный цвет для плановой линии
                backgroundColor: '#FF6347',
                pointRadius: type === 'line' ? 3 : 0,
                pointHoverRadius: type === 'line' ? 5 : 0,
                pointBackgroundColor: '#fff',
                pointBorderColor: '#FF6347',
                borderDash: [5, 5],  // Штриховая линия
                tension: 0.2,
                borderWidth: 2,
            } as any); // Используем any для обхода ограничений типизации
        }
        
        chartRef.current = new ChartJS(canvasRef.current, {
            type,
            data: {
                labels,
                datasets
            },
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: secondaryData && secondaryData.length > 0,
                        position: 'top',
                    },
                    tooltip: {
                        enabled: true,
                        callbacks: {
                            label: function (context) {
                                const idx = context.dataIndex;
                                const datasetIdx = context.datasetIndex;
                                
                                if (datasetIdx === 0 && tooltips && tooltips[idx]) {
                                    return tooltips[idx];
                                } else if (datasetIdx === 1 && secondaryTooltips && secondaryTooltips[idx]) {
                                    return secondaryTooltips[idx];
                                }
                                
                                return `Значение: ${context.parsed.y}`;
                            },
                        },
                    },
                    title: title
                        ? {
                            display: true,
                            text: title,
                        }
                        : undefined,
                },
                scales: {
                    x: {
                        title: {
                            display: false,
                        },
                        ticks: {
                            callback: function (value, index) {
                                return labels[index];
                            },
                            maxRotation: 60,
                            minRotation: 60,
                            autoSkip: true,
                            maxTicksLimit: 53,
                        },
                    },
                    y: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Значение показателя',
                        },

                    },
                },
            }
        });

        // Очистка при размонтировании
        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
            }
        };
    }, [labels, data, secondaryData, secondaryLabel, tooltips, secondaryTooltips, title, type]);

    return (
        <div style={{width: '100%', minHeight: 350}}>
            <canvas ref={canvasRef}/>
        </div>
    );
};